/**
 * OrderDetailPage.tsx
 * Detailed order view with status timeline and tracking
 */

import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useOrder } from '../hooks/useMarketplace';
import { OrderTimeline } from '../components';
import { OrderStatusEnum, type OrderStatus } from '../types/marketplaceTypes';

const ORDER_STATUS_CONFIG: Record<OrderStatus, { label: string; color: string; bg: string }> = {
  ['pending']: { label: 'Pending', color: 'text-[#8B8178]', bg: 'bg-[#8B8178]/10' },
  ['confirmed']: { label: 'Confirmed', color: 'text-[#6B8DD6]', bg: 'bg-[#6B8DD6]/10' },
  ['processing']: { label: 'Processing', color: 'text-[#C4785A]', bg: 'bg-[#C4785A]/10' },
  ['shipped']: { label: 'Shipped', color: 'text-[#7A9E7E]', bg: 'bg-[#7A9E7E]/10' },
  ['delivered']: { label: 'Delivered', color: 'text-[#5C7D60]', bg: 'bg-[#5C7D60]/10' },
  ['cancelled']: { label: 'Cancelled', color: 'text-[#C4785A]', bg: 'bg-[#C4785A]/10' },
  ['refunded']: { label: 'Refunded', color: 'text-[#8B8178]', bg: 'bg-[#8B8178]/10' },
};

export const OrderDetailPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const { data: order, isLoading, error } = useOrder(orderId || '');

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FDF8F3] py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-6 w-32 bg-[#F5EDE4] rounded mb-4" />
            <div className="h-10 w-64 bg-[#F5EDE4] rounded mb-8" />
            <div className="bg-white rounded-xl p-8">
              <div className="h-6 w-48 bg-[#F5EDE4] rounded mb-6" />
              <div className="space-y-4">
                <div className="h-20 bg-[#F5EDE4] rounded" />
                <div className="h-20 bg-[#F5EDE4] rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-[#FDF8F3] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-[#2B3A4D] mb-2">Order Not Found</h1>
          <p className="text-[#8B8178] mb-6">The order you're looking for doesn't exist.</p>
          <Link
            to="/family/orders"
            className="inline-flex items-center px-6 py-3 bg-[#7A9E7E] text-white rounded-xl hover:bg-[#5C7D60] transition-colors"
          >
            View All Orders
          </Link>
        </div>
      </div>
    );
  }

  const statusConfig = ORDER_STATUS_CONFIG[order.status];

  return (
    <div className="min-h-screen bg-[#FDF8F3] py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <nav className="flex items-center gap-2 text-sm text-[#8B8178] mb-4">
            <Link to="/family/orders" className="hover:text-[#7A9E7E]">Orders</Link>
            <span>/</span>
            <span className="text-[#2B3A4D]">#{order.order_number}</span>
          </nav>

          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-semibold text-[#2B3A4D] font-['Fraunces']">
                Order #{order.order_number}
              </h1>
              <p className="text-[#8B8178] mt-1">
                Placed on {new Date(order.ordered_at || order.created_at).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
            <span className={`px-4 py-2 rounded-full text-sm font-medium ${statusConfig.color} ${statusConfig.bg}`}>
              {statusConfig.label}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Order Timeline */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-[#2B3A4D] mb-6">Order Status</h2>
              <OrderTimeline order={order} />
            </div>

            {/* Product Details */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-[#2B3A4D] mb-4">Order Details</h2>
              
              <div className="flex items-start gap-4 p-4 bg-[#FDF8F3] rounded-lg">
                {/* Product Image Placeholder */}
                <div className="w-20 h-20 rounded-lg bg-[#F5EDE4] flex items-center justify-center flex-shrink-0">
                  <svg className="w-10 h-10 text-[#8B8178]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-[#2B3A4D]">{order.description || 'Product'}</h3>
                  <p className="text-sm text-[#8B8178]">Quantity: {order.quantity}</p>
                  <Link
                    to={`/marketplace/products/${order.product_id}`}
                    className="text-sm text-[#7A9E7E] hover:text-[#5C7D60] font-medium mt-1 inline-block"
                  >
                    View Product →
                  </Link>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-[#2B3A4D]">
                    ${order.subtotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>

            {/* Shipping Info */}
            {order.shipping_address && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-[#2B3A4D] mb-4">Shipping Address</h2>
                <div className="text-[#445566]">
                  <p>{order.shipping_address.name}</p>
                  <p>{order.shipping_address.street}</p>
                  {order.shipping_address.street2 && <p>{order.shipping_address.street2}</p>}
                  <p>
                    {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}
                  </p>
                  <p>{order.shipping_address.country}</p>
                </div>
              </div>
            )}

            {/* Tracking */}
            {order.tracking_number && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-[#2B3A4D] mb-4">Tracking Information</h2>
                <div className="flex items-center gap-4 p-4 bg-[#7A9E7E]/5 rounded-lg">
                  <svg className="w-6 h-6 text-[#7A9E7E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                  </svg>
                  <div>
                    <p className="font-medium text-[#2B3A4D]">Tracking Number</p>
                    <p className="text-[#7A9E7E] font-mono">{order.tracking_number}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Notes */}
            {order.customer_notes && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-[#2B3A4D] mb-4">Order Notes</h2>
                <p className="text-[#445566]">{order.customer_notes}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Order Summary */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-[#2B3A4D] mb-4">Order Summary</h2>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#8B8178]">Subtotal</span>
                  <span className="text-[#2B3A4D]">
                    ${(order.subtotal ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#8B8178]">Shipping</span>
                  <span className="text-[#2B3A4D]">
                    ${(order.shipping_cost ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#8B8178]">Tax</span>
                  <span className="text-[#2B3A4D]">
                    ${(order.tax ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                {order.discount && order.discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount</span>
                    <span>
                      -${order.discount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                <div className="border-t border-[#E5E0DB] pt-3 flex justify-between font-semibold">
                  <span className="text-[#2B3A4D]">Total</span>
                  <span className="text-[#2B3A4D]">
                    ${(order.total ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>

              {order.insurance_claim_id && (
                <div className="mt-4 pt-4 border-t border-[#E5E0DB]">
                  <p className="text-sm text-[#8B8178]">Insurance Claim</p>
                  <p className="font-medium text-[#7A9E7E]">{order.insurance_claim_id}</p>
                </div>
              )}
            </div>

            {/* Vendor Info */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-[#2B3A4D] mb-4">Vendor</h2>
              <Link
                to={`/marketplace/vendors/${order.vendor_id}`}
                className="flex items-center gap-3 p-3 bg-[#FDF8F3] rounded-lg hover:bg-[#F5EDE4] transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-[#7A9E7E]/10 flex items-center justify-center">
                  <svg className="w-5 h-5 text-[#7A9E7E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-[#2B3A4D]">View Vendor</p>
                  <p className="text-sm text-[#8B8178]">Contact & more products</p>
                </div>
              </Link>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-[#2B3A4D] mb-4">Need Help?</h2>
              <div className="space-y-3">
                <button className="w-full px-4 py-3 text-left bg-[#FDF8F3] rounded-lg hover:bg-[#F5EDE4] transition-colors flex items-center gap-3">
                  <svg className="w-5 h-5 text-[#7A9E7E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <span className="font-medium text-[#2B3A4D]">Contact Vendor</span>
                </button>
                <button className="w-full px-4 py-3 text-left bg-[#FDF8F3] rounded-lg hover:bg-[#F5EDE4] transition-colors flex items-center gap-3">
                  <svg className="w-5 h-5 text-[#7A9E7E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-medium text-[#2B3A4D]">Get Support</span>
                </button>
                {order.status !== OrderStatus.CANCELLED && order.status !== OrderStatus.DELIVERED && (
                  <button className="w-full px-4 py-3 text-left bg-[#C4785A]/5 rounded-lg hover:bg-[#C4785A]/10 transition-colors flex items-center gap-3">
                    <svg className="w-5 h-5 text-[#C4785A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="font-medium text-[#C4785A]">Cancel Order</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;




